import { CREATOR_EVENT_DOWN, CREATOR_EVENT_MOVE, CREATOR_EVENT_OUT, CREATOR_EVENT_TOUCHCANCEL, CREATOR_EVENT_TOUCHEND, CREATOR_EVENT_TOUCHMOVE, CREATOR_EVENT_TOUCHSTART, CREATOR_EVENT_UP, } from '../AnnotationCreator';
import { AnnotationUtil } from '../AnnotationUtil';
import { AnnotationRendererV3 } from './AnnotationRendererV3';
import { AnnotationSchemaV3 } from './AnnotationSchemaV3';
import { TextAnnotationComponent } from './TextAnnotationComponent';
const VALUE_NOT_FOUND = -1;
export class AnnotationCreatorV3 extends AnnotationRendererV3 {
    constructor(element, annotations, helper) {
        super(element, annotations, helper);
        this.schema = AnnotationSchemaV3;
        this.toolType = null;
        this.toolSize = null;
        this.toolColor = null;
        //need for saving pointerId for disabling multi-touch drawing
        this.eventId = null;
        // Holders for events
        this.bindedEventAnnotationStart = this._onEventAnnotationStart.bind(this);
        this.bindedEventAnnotationEnd = this._onEventAnnotationEnd.bind(this);
        this.bindedEventAnnotationMove = this._onEventAnnotationMove.bind(this);
        this.bindedEventTextStart = this._onEventTextStart.bind(this);
        this.bindedEventTextEnd = this._onEventTextEnd.bind(this);
        this.bindedEventTextMove = this._onEventTextMove.bind(this);
        this.bindedEventLeavePage = this._onEventLeavePage.bind(this);
        this.zoomRatio = helper.options.devicePixelRatio;
        this.creatingTextbox = null;
        this.isTextboxActive = false;
        this._isCreatingAnnotation = false;
        this.textBoxMap = {}; // map holding all temp text annotations
        this.enabled = true;
        this.listeners = {};
    }
    /**
     * Generate a unique id
     * @returns {number}
     * @private
     */
    _uniqId() {
        return Date.now() + Math.random();
    }
    /**
     * Create the canvas for the creator
     */
    _createCanvas() {
        this.canvas = AnnotationUtil.createCanvasForElement(this.element, this.helper.options.devicePixelRatio, this.helper.options.append, true);
        this.canvas.className += ' annotation-creator';
        this.context = this.canvas.getContext('2d');
    }
    /**
     * Callback on start of drawing a new (free) annotation.
     * This will be triggered on CREATE_EVENT_DOWN (pressing mouse button)
     * @param event
     * @private
     */
    _onEventAnnotationStart(event) {
        if (this.getIsEnabled() &&
            (event.button === 0 || event.type === CREATOR_EVENT_TOUCHSTART) &&
            !this._isCreatingAnnotation) {
            this.eventId = event.pointerId;
            this._isCreatingAnnotation = true;
            document.body.addEventListener(CREATOR_EVENT_TOUCHEND, this.bindedEventAnnotationEnd, true);
            document.body.addEventListener(CREATOR_EVENT_UP, this.bindedEventAnnotationEnd, true);
            window.addEventListener(CREATOR_EVENT_TOUCHCANCEL, this.bindedEventLeavePage, true);
            window.addEventListener(CREATOR_EVENT_OUT, this.bindedEventLeavePage, true);
            this.canvas.addEventListener(CREATOR_EVENT_TOUCHMOVE, this.bindedEventAnnotationMove, true);
            this.canvas.addEventListener(CREATOR_EVENT_MOVE, this.bindedEventAnnotationMove, true);
            this.addAnnotationPoint(event);
        }
    }
    /**
     * Callback for the stopping of drawing a (free) annotation
     * Will be triggered on CREATE_EVENT_UP (releasing mouse button)
     * @private
     */
    _onEventAnnotationEnd() {
        this._isCreatingAnnotation = false;
        document.body.removeEventListener(CREATOR_EVENT_TOUCHEND, this.bindedEventAnnotationEnd);
        document.body.removeEventListener(CREATOR_EVENT_UP, this.bindedEventAnnotationEnd);
        window.removeEventListener(CREATOR_EVENT_TOUCHCANCEL, this.bindedEventLeavePage);
        window.removeEventListener(CREATOR_EVENT_OUT, this.bindedEventLeavePage);
        this.canvas.removeEventListener(CREATOR_EVENT_TOUCHMOVE, this.bindedEventAnnotationMove);
        this.canvas.removeEventListener(CREATOR_EVENT_MOVE, this.bindedEventAnnotationMove);
        // Notify listeners of new annotation
        this.notify('annotation', this.annotations[0]);
        // Clear drawn annotation + canvas
        this.annotations = [];
        this.clear();
    }
    /**
     * Callback when moving when a new annotations is being created
     * Will be triggered on CREATE_EVENT_MOVE when CREATE_EVENT_DOWN has been triggered (moving the mouse when button
     * is held down)
     * @param event
     * @private
     */
    _onEventAnnotationMove(event) {
        if (!event.pointerId || event.pointerId === this.eventId) {
            this.addAnnotationPoint(event);
        }
    }
    /**
     * Add a new point to the annotation based on event data
     * @param event
     * @param {boolean} addRectOffset
     */
    addAnnotationPoint(event, addRectOffset = false) {
        if (!this._isCreatingAnnotation) {
            return;
        }
        const self = this;
        const eventX = getEventX();
        const eventY = getEventY();
        const devicePixelRatio = this.helper.options.devicePixelRatio;
        const x = eventX / (this.width / devicePixelRatio);
        const y = eventY / (this.height / devicePixelRatio);
        let lastPoint = null;
        validate();
        addFirstPoint();
        if (this.annotations[0] && this.annotations[0].points && this.annotations[0].points.length > 0) {
            lastPoint = this.annotations[0].points[this.annotations.length - 1];
        }
        if (lastPoint === null || lastPoint[0] !== x || lastPoint[1] !== y) {
            this.annotations[0].points.push([x, y]);
            this.render();
        }
        /**
         * Get rect x position
         * @returns {number}
         */
        function getRectX() {
            let rectX = 0;
            if (addRectOffset) {
                rectX = event.currentTarget.offsetLeft;
            }
            return rectX;
        }
        /**
         * Get rect y position
         * @returns {number}
         */
        function getRectY() {
            let rectY = 0;
            if (addRectOffset) {
                rectY = event.currentTarget.offsetTop;
            }
            return rectY;
        }
        /**
         * Get event x position
         * @returns {number}
         */
        function getEventX() {
            let eventX = 0;
            if (event.offsetX) {
                eventX = event.offsetX;
            }
            else if (event.touches) {
                eventX = event.touches[0].pageX - event.touches[0].target.getBoundingClientRect().left;
            }
            else {
                eventX = event.layerX;
            }
            return eventX + getRectX();
        }
        /**
         * Get event y position
         * @returns {number}
         */
        function getEventY() {
            let result = 0;
            if (event.offsetY) {
                result = event.offsetY;
            }
            else if (event.touches) {
                result = event.touches[0].pageY - event.touches[0].target.getBoundingClientRect().top;
            }
            else {
                result = event.layerY;
            }
            return result + getRectY();
        }
        /**
         * Validate state
         */
        function validate() {
            // Validation
            if (self.toolType === null) {
                throw new Error('Annotation type not initialized');
            }
            if (self.toolSize === null) {
                throw new Error('Annotation size not initialized');
            }
            if (self.toolColor === null) {
                throw new Error('Annotation color not initialized');
            }
        }
        /**
         * Add first point if needed
         */
        function addFirstPoint() {
            if (self.annotations.length === 0) {
                self.annotations.push({
                    type: self.toolType,
                    color: self.toolColor,
                    size: self.toolSize,
                    points: [],
                });
            }
        }
    }
    /**
     * This method restores textbox object.
     *
     * @param data - data object contains dimensions, toolSize, toolColor. This data object is taken from undo stack
     * @param id - id of restored text box which was indicated in undo stack
     */
    restoreTextBox(data, id) {
        const parentRect = this.element.getBoundingClientRect();
        const dimensions = {
            left: parentRect.width * data.points[0][0],
            top: parentRect.height * data.points[0][1],
            width: (data.points[1][0] - data.points[0][0]) * parentRect.width,
            height: (data.points[1][1] - data.points[0][1]) * parentRect.height,
        };
        const textBox = new TextAnnotationComponent(dimensions, data.size, data.color, this);
        textBox.id = id;
        textBox.create(this.element, true, data.text);
        this.textBoxMap[id] = textBox;
    }
    _onEventTextStart(event) {
        const self = this;
        let textAnnotations = this.findActiveTextAnnotations();
        if (this.getIsEnabled() && event.button === 0) {
            // Find active textbox, if found, just close it off
            // We need the count, so we can't just close them all at once
            if (textAnnotations.length > 0) {
                finishTextAnnotations();
            }
            else {
                // Add a new textbox, and add listeners for closing / moving it
                createNewTextBox();
            }
        }
        this.notify('settings-updated');
        /**
         * Close of all text annotations
         */
        function finishTextAnnotations() {
            textAnnotations.forEach((textAnnotation) => {
                textAnnotation.finish();
            });
        }
        /**
         * Start a new text box
         */
        function createNewTextBox() {
            const textBoxId = self._uniqId();
            const fontSize = self.toolSize * self.zoomRatio * 1000;
            //align middle of text field with the cursor: height = fontSize + 15% margin
            const height = fontSize * 1.15;
            const topPosition = Math.max(getTop() - height / 2, 0);
            const dimensions = {
                left: getLeft(),
                top: topPosition,
                width: 200,
                height,
            };
            const textBox = new TextAnnotationComponent(dimensions, self.toolSize, self.toolColor, self);
            textBox.id = textBoxId;
            textBox.create(self.element, true, null);
            self.textBoxMap[textBoxId] = textBox;
            self.notify('annotation', {
                type: self.schema.TYPE_TEXT_TMP,
                id: textBoxId,
                points: [],
                restoreData: {
                    dimensions: dimensions,
                    toolSize: self.toolSize,
                    toolColor: self.toolColor,
                },
            });
            /**
             * Get offset left
             * @returns {number}
             */
            function getLeft() {
                let result = null;
                if (event.offsetX) {
                    result = event.offsetX;
                }
                else {
                    result = event.layerX;
                }
                return result;
            }
            /**
             * Get offset top
             * @returns {number}
             */
            function getTop() {
                let result = null;
                if (event.offsetY) {
                    result = event.offsetY;
                }
                else {
                    result = event.layerY;
                }
                return result;
            }
        }
    }
    _onEventTextEnd() {
        // not implemented
    }
    _onEventTextMove() {
        // not implemented
    }
    /**
     * Stop annotating when leaving the page
     * @param event
     * @private
     */
    _onEventLeavePage(event) {
        var element = event.relatedTarget || event.toElement;
        if (!element || element.nodeName === 'HTML') {
            this._onEventAnnotationEnd();
        }
    }
    /**
     * Returns current used schema
     * @returns {object}
     */
    getSchema() {
        return this.schema;
    }
    /**
     * Set the zoom ratio of the creator (needed for text annotations)
     * @param zoomRatio
     */
    setZoomRatio(zoomRatio) {
        if (zoomRatio !== this.setZoomRatio) {
            this.zoomRatio = zoomRatio;
            this._updateTextBoxes();
        }
    }
    /**
     * Set the tool type of the creator
     * @param toolType
     */
    setToolType(toolType) {
        const self = this;
        validateToolType();
        // Remove old listener
        if (this.toolType === this.schema.TYPE_TEXT) {
            setToolTypeText();
        }
        else if (this.toolType !== null) {
            setToolTypeNotText();
        }
        this.toolType = toolType;
        // Add new listener
        if (toolType === this.schema.TYPE_TEXT) {
            this.canvas.addEventListener(CREATOR_EVENT_DOWN, this.bindedEventTextStart);
        }
        else {
            this.finishTextAnnotations();
            this.canvas.addEventListener(CREATOR_EVENT_TOUCHSTART, this.bindedEventAnnotationStart);
            this.canvas.addEventListener(CREATOR_EVENT_DOWN, this.bindedEventAnnotationStart);
        }
        this.notify('settings-updated');
        /**
         * Validate the given tool type
         */
        function validateToolType() {
            if (self.schema.TYPES.indexOf(toolType) === VALUE_NOT_FOUND) {
                throw new Error('Invalid tool type specified');
            }
        }
        /**
         * Update the tool type for type text
         */
        function setToolTypeText() {
            if (self.creatingTextbox !== null) {
                self.creatingTextbox.finish();
                self.creatingTextbox = null;
            }
            self.canvas.removeEventListener(CREATOR_EVENT_DOWN, self.bindedEventTextStart);
        }
        /**
         * Remove event listener for type not text
         */
        function setToolTypeNotText() {
            self.canvas.removeEventListener(CREATOR_EVENT_TOUCHSTART, self.bindedEventAnnotationStart);
            self.canvas.removeEventListener(CREATOR_EVENT_DOWN, self.bindedEventAnnotationStart);
        }
    }
    /**
     * Get the current tool type of the creator
     * @returns {AnnotationCreatorV3.toolType}
     */
    getToolType() {
        return this.toolType;
    }
    /**
     * Set the tool size of the creator
     * @param toolSize
     */
    setToolSize(toolSize) {
        if (this.toolType !== null) {
            if (this.schema.TYPE_SIZES[this.toolType].indexOf(toolSize) > VALUE_NOT_FOUND) {
                this.toolSize = toolSize;
                this.setActiveTextBoxSize(toolSize);
                this.notify('settings-updated');
            }
        }
    }
    /**
     * Get the current tool size of the creator
     * @returns {AnnotationCreatorV3.toolSize}
     */
    getToolSize() {
        return this.toolSize;
    }
    /**
     * Set the tool size of the creator
     * @param {string} toolColor
     */
    setToolColor(toolColor) {
        if (this.schema.COLORS.indexOf(toolColor) > VALUE_NOT_FOUND) {
            this.toolColor = toolColor;
            this.setActiveTextBoxColor(toolColor);
            this.notify('settings-updated');
        }
    }
    /**
     * Set the color of the active textbox
     * @param {string} toolColor
     */
    setActiveTextBoxColor(toolColor) {
        let activeTextBoxes = this.findActiveTextAnnotations();
        if (activeTextBoxes.length) {
            for (let index in activeTextBoxes) {
                if (activeTextBoxes.hasOwnProperty(index)) {
                    activeTextBoxes[index].setColor(toolColor);
                    activeTextBoxes[index].setFocus();
                }
            }
        }
    }
    /**
     * Set the size of the active textbox
     * @param {number} toolSize
     */
    setActiveTextBoxSize(toolSize) {
        if (this.toolType === this.schema.TYPE_TEXT) {
            let activeTextBoxes = this.findActiveTextAnnotations();
            if (activeTextBoxes.length) {
                updateTextboxSizes(activeTextBoxes);
            }
        }
        /**
         * Update size for textboxes
         * @param {Array.<TextAnnotation>} activeTextBoxes
         */
        function updateTextboxSizes(activeTextBoxes) {
            for (let index in activeTextBoxes) {
                if (activeTextBoxes.hasOwnProperty(index)) {
                    activeTextBoxes[index].setSize(toolSize);
                    activeTextBoxes[index].setFocus();
                }
            }
        }
    }
    /**
     * Get the current textbox color id
     * @returns {AnnotationCreatorV3.toolColor}
     */
    getActiveTextBoxColorId() {
        let activeTextBox = this.findActiveTextAnnotations()[0];
        if (activeTextBox) {
            let activeColor = activeTextBox.getColor();
            let currentColorIndex = this.schema.COLORS.indexOf(activeColor);
            if (currentColorIndex >= 0) {
                // Color id is not zero based so add 1
                return `color-${currentColorIndex + 1}`;
            }
        }
        return 0;
    }
    /**
     * Get the current textbox color id
     * @returns {AnnotationCreatorV3.toolColor}
     */
    getActiveTextBoxColorSizeIndex() {
        let activeTextBox = this.findActiveTextAnnotations()[0];
        let currentSizeIndex;
        if (activeTextBox) {
            let activeSize = activeTextBox.getSize();
            currentSizeIndex = this.schema.TYPE_SIZES.TEXT.indexOf(activeSize);
        }
        return currentSizeIndex;
    }
    /**
     * Get the current tool color of the creator
     * @returns {AnnotationCreatorV3.toolColor}
     */
    getToolColor() {
        return this.toolColor;
    }
    /**
     * Check if the user is creating a textbox
     * @returns {boolean}
     */
    isCreatingText() {
        return this.isTextboxActive;
    }
    setTextboxActive(active) {
        this.isTextboxActive = active;
        this.notify('settings-updated');
    }
    /**
     * Enable or disable the creator
     * @param {boolean} isEnabled
     */
    setIsEnabled(isEnabled) {
        if (isEnabled !== true && isEnabled !== false) {
            throw 'Invalid state';
        }
        if (isEnabled !== this.enabled) {
            this.enabled = isEnabled;
            this.notify('settings-updated');
        }
        if (!isEnabled) {
            this.finishTextAnnotations();
        }
    }
    /**
     * Get the state of the creator
     * @returns {boolean}
     */
    getIsEnabled() {
        return this.enabled;
    }
    /**
     * Hide a specific text box
     * @param id
     * @param individual whether this is a single box being hidden (undo) or all boxes (clear canvas) - needs to be
     *     reversible
     */
    hideTextBox(id, individual) {
        if (this.textBoxMap[id]) {
            this.textBoxMap[id].hide(individual);
        }
    }
    /**
     * Show a specific text box
     * @param id
     * @param individual whether this is an individual box being shown or all boxes being shown - needs to be reversible
     */
    showTextBox(id, individual) {
        if (this.textBoxMap[id]) {
            this.textBoxMap[id].show(individual);
        }
    }
    /**
     * Update textboxes ratio + positions
     * @private
     */
    _updateTextBoxes() {
        for (let index in this.textBoxMap) {
            if (this.textBoxMap.hasOwnProperty(index)) {
                this.textBoxMap[index].setRatio(this.zoomRatio);
            }
        }
    }
    /**
     * Update Creator dimensions + optionally update the zoomRatio
     * @param zoomRatio optional
     */
    resize(zoomRatio) {
        super.resize();
        if (typeof zoomRatio === 'number') {
            this.setZoomRatio(zoomRatio);
        }
    }
    /**
     * Return the text annotation from the tmp boxes
     */
    getTextBoxAnnotations() {
        var annotations = [];
        for (let index in this.textBoxMap) {
            if (this.textBoxMap.hasOwnProperty(index)) {
                annotations.push(this.textBoxMap[index].getData());
            }
        }
        return annotations;
    }
    /**
     *
     * @returns {_isCreatingAnnotation}
     */
    isCreatingAnnotation() {
        return this._isCreatingAnnotation;
    }
    /**
     * Loop over all annotations and call 'finish' on them
     * @array skipTextboxIds
     */
    finishTextAnnotations(skipTextboxIds = []) {
        for (const index in this.textBoxMap) {
            if (this.textBoxMap.hasOwnProperty(index) && skipTextboxIds.indexOf(index) === VALUE_NOT_FOUND) {
                this.textBoxMap[index].finish();
            }
        }
    }
    /**
     * Loop over text annotations, return active ones
     */
    findActiveTextAnnotations() {
        let index;
        let textAnnotation;
        let activeAnnotations = [];
        for (index in this.textBoxMap) {
            if (this.textBoxMap.hasOwnProperty(index)) {
                textAnnotation = this.textBoxMap[index];
                if (textAnnotation._editing) {
                    activeAnnotations.push(textAnnotation);
                }
            }
        }
        return activeAnnotations;
    }
    /**
     * Focus the next textbox in line
     * @param {Number} currentTextBoxId
     */
    focusNextTextBox(currentTextBoxId) {
        currentTextBoxId = Number(currentTextBoxId);
        const textBoxKeys = Object.keys(this.textBoxMap).map(Number);
        const currentIndex = textBoxKeys.indexOf(currentTextBoxId);
        const nextIndex = currentIndex === textBoxKeys.length - 1 ? 0 : currentIndex + 1;
        const nextKey = textBoxKeys[nextIndex];
        // trigger click;
        if (this.textBoxMap[nextKey]._boundClosedTextAnnotationClick) {
            this.textBoxMap[nextKey]._boundClosedTextAnnotationClick();
        }
    }
    /**
     * Focus the previous textbox
     * @param {Number} currentTextBoxId
     */
    focusPreviousTextBox(currentTextBoxId) {
        currentTextBoxId = Number(currentTextBoxId);
        const textBoxKeys = Object.keys(this.textBoxMap).map(Number);
        const currentIndex = textBoxKeys.indexOf(currentTextBoxId);
        const previousIndex = currentIndex === 0 ? textBoxKeys.length - 1 : currentIndex - 1;
        const previousKey = textBoxKeys[previousIndex];
        // trigger click;
        if (this.textBoxMap[previousKey]._boundClosedTextAnnotationClick) {
            this.textBoxMap[previousKey]._boundClosedTextAnnotationClick();
        }
    }
    /**
     * Subscribe on certain events
     * @param eventName
     * @param callback
     */
    on(eventName, callback) {
        if (!(eventName in this.listeners)) {
            this.listeners[eventName] = [];
        }
        this.listeners[eventName].push(callback);
    }
    /**
     * Notify subscribers on certain events
     * @param eventName
     * @param data
     */
    notify(eventName, data = null) {
        if (eventName in this.listeners) {
            this.listeners[eventName].forEach((callback) => {
                try {
                    callback.call(callback, data);
                }
                catch (e) {
                    // ignore
                }
            });
        }
    }
}
//# sourceMappingURL=AnnotationCreatorV3.js.map